import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type SubscriptionTier = 'none' | 'starter' | 'growth' | 'enterprise';

interface TierConfig {
  level: number;
  label: string;
}

interface SubscriptionContextType {
  currentTier: SubscriptionTier;
  tierLevel: number;
  tierLabel: string;
  hasAccess: (requiredTier: SubscriptionTier) => boolean;
  setTier: (tier: SubscriptionTier) => void;
}

const TIER_CONFIG: Record<SubscriptionTier, TierConfig> = {
  none: { level: 0, label: "No Active Plan" },
  starter: { level: 1, label: "Automation Starter" },
  growth: { level: 2, label: "Growth Plan" },
  enterprise: { level: 3, label: "Enterprise / Elite" }
};

const STORAGE_KEY = "neilai_subscription_tier";
const URL_TIER_PARAM = "plan";
const DEFAULT_TIER: SubscriptionTier = "none";

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [currentTier, setCurrentTier] = useState<SubscriptionTier>(DEFAULT_TIER);

  useEffect(() => {
    // Check URL for tier
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get(URL_TIER_PARAM);
    
    if (planParam && planParam in TIER_CONFIG) {
      const tier = planParam.toLowerCase() as SubscriptionTier;
      setCurrentTier(tier);
      try {
        localStorage.setItem(STORAGE_KEY, tier);
      } catch (e) {
        console.error('Failed to save tier to localStorage', e);
      }
      return;
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored in TIER_CONFIG) {
        setCurrentTier(stored as SubscriptionTier);
        return;
      }
    } catch (e) {
      console.error('Failed to read tier from localStorage', e);
    }

    setCurrentTier(DEFAULT_TIER);
  }, []);

  const setTier = (tier: SubscriptionTier) => {
    setCurrentTier(tier);
    try {
      localStorage.setItem(STORAGE_KEY, tier);
    } catch (e) {
      console.error('Failed to save tier to localStorage', e);
    }
  };

  const tierConfig = TIER_CONFIG[currentTier];
  const hasAccess = (requiredTier: SubscriptionTier) => {
    const requiredLevel = TIER_CONFIG[requiredTier].level;
    return tierConfig.level >= requiredLevel;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        currentTier,
        tierLevel: tierConfig.level,
        tierLabel: tierConfig.label,
        hasAccess,
        setTier
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
